import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ITEM_PROPERTIES_QUERY } from '../../queries';
import { DELETE_ITEM_PROPERTY } from '../../mutations';
import { ITEM_PROPERTY_REMOVED } from '../../subscriptions';
import ItemPropertiesComponent from './ItemPropertiesComponent';
import ItemPropertyModalComponent from './ItemPropertyModalComponent';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { editItemPropertyVar } from 'features/items/edit/variables';

const ItemPropertiesContainer = ({ itemId }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  // #region queries
  const { loading, data } = useQuery(ITEM_PROPERTIES_QUERY, {
    variables: {
      id: itemId
    },
    skip: !itemId
  });
  // #endregion

  // #region mutations
  const [deleteItemProperty] = useMutation(DELETE_ITEM_PROPERTY);
  // #endregion

  // #region subscriptions
  useSubscription(ITEM_PROPERTY_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Items.Properties.PropertyRemoved'), 'success');
      client.cache.evict({ id: `ItemProperty:{"propertyId":${data.itemPropertyRemoved.propertyId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'itemProperties' });
      client.cache.gc();
    }
  });
  // #endregion

  // #region handlers
  const handleDeleteProperty = useCallback(
    propertyId => {
      confirm(
        () => {
          deleteItemProperty({ variables: { input: { itemId: itemId, propertyId: propertyId } } });
        },
        { content: t('Items.DeleteItemProperty') }
      );
    },
    [confirm, deleteItemProperty, itemId, t]
  );

  const handleEditProperty = useCallback(property => {
    editItemPropertyVar({ open: true, property: property, isNew: false });
  }, []);

  const handleAddNewProperty = useCallback(() => {
    editItemPropertyVar({ open: true, property: { itemId }, isNew: true });
  }, [itemId]);
  // #endregion

  return (
    <>
      <ItemPropertiesComponent
        data={data?.itemProperties || []}
        loading={loading}
        onDelete={handleDeleteProperty}
        onEdit={handleEditProperty}
        onAddNew={handleAddNewProperty}
      />
      <ItemPropertyModalComponent itemProperties={data?.itemProperties || []} />
    </>
  );
};

ItemPropertiesContainer.propTypes = {
  itemId: PropTypes.number
};

export default ItemPropertiesContainer;
